import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { getOverlayRoot, getTabbable, isTopOverlay, placePopover, pushOverlay, removeOverlay, spanRect, type Placement } from './overlayCore';
import { useDocumentDir, useLatest, usePresence } from './useOverlay';

/**
 * Anchored floating layer (T-28, PRD §4.7): `mat-float`, `rounded-xl`, grows
 * from its trigger, logical `start` / `end` alignment (correct in RTL and LTR),
 * flips above the trigger when there is no room below, clamps to the viewport.
 *
 * Portalled (so the glass top bar's backdrop-filter never traps it) into the
 * overlay root, or into the dialog layer that contains the trigger, so a
 * popover opened inside a dialog stacks above it. Escape (topmost only) and
 * Tab out close it and return focus to the trigger; an outside press closes it.
 * Enter and exit motion, cross-fade under reduced motion.
 */
export type PopoverCloseReason = 'escape' | 'outside' | 'tab' | 'select';

export interface PopoverProps {
  open: boolean;
  onClose: (reason: PopoverCloseReason) => void;
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  /** Which inline edge of the trigger the popover lines up with. */
  align?: 'start' | 'end';
  id?: string;
  /** Width / padding classes for the panel (default `w-72 p-1.5`). */
  className?: string;
  /** role + name when the content is not itself a menu (e.g. the notifications panel). */
  role?: 'dialog' | 'group';
  ariaLabel?: string;
  ariaLabelledBy?: string;
  /** The element whose text describes the panel (read after its name). */
  ariaDescribedBy?: string;
  /** Focus the first focusable element on open (default false: a Menu focuses itself). */
  focusFirst?: boolean;
  /**
   * Take the width and the inline edges of this element instead of the class
   * width, still opening under (or above) the trigger: a phone dropdown that
   * spans the whole card under one tile.
   */
  widthRef?: RefObject<HTMLElement | null>;
}

const EXIT_MS = 220;

export default function Popover({
  open,
  onClose,
  anchorRef,
  children,
  align = 'end',
  id,
  className = 'w-72 p-1.5',
  role,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  focusFirst = false,
  widthRef,
}: PopoverProps) {
  const { mounted, state } = usePresence(open, EXIT_MS);
  const dir = useDocumentDir();
  const panelRef = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<(Placement & { width?: number }) | null>(null);
  const overlayId = useId();
  const onCloseRef = useLatest(onClose);

  // Position: measure, place, and follow resize/scroll while mounted.
  useLayoutEffect(() => {
    if (!mounted) { setPlace(null); return; }
    const update = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      const r = anchor.getBoundingClientRect();
      let rect = { top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height };
      const spanEl = widthRef?.current;
      let width: number | undefined;
      if (spanEl) {
        const w = spanEl.getBoundingClientRect();
        rect = spanRect(rect, { top: w.top, left: w.left, right: w.right, bottom: w.bottom, width: w.width, height: w.height });
        width = Math.round(w.width);
        panel.style.width = `${width}px`; // measure the height at the final width
      }
      // The phone tab bar (html scroll-padding-bottom) is covered space: a popover never hides it.
      const padBottom = parseFloat(getComputedStyle(document.documentElement).scrollPaddingBottom) || 0;
      const next = {
        ...placePopover(rect, { width: width ?? panel.offsetWidth, height: panel.scrollHeight },
          { width: window.innerWidth, height: window.innerHeight - padBottom }, { align, dir }),
        width,
      };
      setPlace((prev) => (prev && prev.top === next.top && prev.left === next.left && prev.side === next.side
        && prev.maxHeight === next.maxHeight && prev.width === next.width ? prev : next));
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    // Content that loads after opening (a list replacing its skeleton) changes
    // the height: place again so a popover above the trigger never covers it.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => update()) : null;
    if (ro && panelRef.current) ro.observe(panelRef.current);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      ro?.disconnect();
    };
  }, [mounted, anchorRef, widthRef, align, dir]);

  // Open: stack, outside press, Escape, Tab out, optional first focus.
  useEffect(() => {
    if (!open) return;
    pushOverlay(overlayId);
    const close = (reason: PopoverCloseReason, refocus: boolean) => {
      onCloseRef.current(reason);
      if (refocus) anchorRef.current?.focus({ preventScroll: true });
    };
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      // A nested overlay (e.g. a picker opened inside the filters panel, FIX-B21 QX-169) is portalled
      // outside this panel: only the topmost overlay answers an outside press, like Escape.
      if (!isTopOverlay(overlayId)) return;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      close('outside', false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (!isTopOverlay(overlayId)) return;
      const panel = panelRef.current;
      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault();
        close('escape', true);
        return;
      }
      if (e.key === 'Tab' && panel && panel.contains(document.activeElement)) {
        const items = getTabbable(panel);
        const i = items.indexOf(document.activeElement as HTMLElement);
        const leaving = items.length === 0 || (e.shiftKey ? i <= 0 : i === items.length - 1);
        if (leaving) { e.preventDefault(); close('tab', true); }
      }
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    let raf = 0;
    if (focusFirst) {
      raf = requestAnimationFrame(() => {
        const panel = panelRef.current;
        if (!panel) return;
        (getTabbable(panel)[0] ?? panel).focus({ preventScroll: true });
      });
    }
    return () => {
      cancelAnimationFrame(raf);
      removeOverlay(overlayId);
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, overlayId, anchorRef, onCloseRef, focusFirst]);

  if (!mounted || typeof document === 'undefined') return null;

  const container = (anchorRef.current?.closest('[data-overlay-layer]') as HTMLElement | null) ?? getOverlayRoot();
  const style: CSSProperties = place
    ? { top: place.top, left: place.left, maxHeight: place.maxHeight, width: place.width, transformOrigin: `${place.side === 'bottom' ? 'top' : 'bottom'} ${place.originX}` }
    : { top: 0, left: 0, visibility: 'hidden' };

  return createPortal(
    <div
      ref={panelRef}
      id={id}
      role={role}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      tabIndex={-1}
      className={`ov-popover mat-float fixed z-popover overflow-y-auto overscroll-contain focus:outline-none ${className}`}
      data-state={place ? state : 'closed'}
      data-side={place?.side ?? 'bottom'}
      style={style}
    >
      {children}
    </div>,
    container,
  );
}
